import { FC } from 'react';
import { useProductCardPrice } from '../../../@types/codegen/page';
import { useTranslation } from 'next-i18next';
import { TextOnlyErrorMessage } from '../../error-message/text-only-error-message';
import { CardLoginHint } from '../../product/card/card-login-hint';
import { PriceInformationDomain, ProductPrice } from './ProductPrice';
import { Loader } from '@digando/common-ui';
import layout from './product-card-price.module.scss';
import { IDateRange } from '../../../interfaces/date-range.interface';
import React from 'react';
import { useSession } from 'next-auth/react';
import { LoginStates } from '../../../store/auth.context';

type DynamicProductCardPriceProps = {
  productId: string;
  dateRange: IDateRange;
};

type ProductCardPriceProps = DynamicProductCardPriceProps & {
  showPrices: boolean;
  hasHiddenPrice: boolean;
  previewPriceInformation: PriceInformationDomain | null;
};

/**
 * The DynamicProductCardPrice component is responsible for fetching the product price.
 * It does not display the price directly.
 */
const DynamicProductCardPrice: FC<DynamicProductCardPriceProps> = ({ productId, dateRange }) => {
  const [t] = useTranslation(['checkout-page']);

  const { data, error, loading } = useProductCardPrice(() => {
    return {
      variables: {
        productId: productId,
        dateRange: {
          gte: dateRange.gte.format('YYYY-MM-DD'),
          lte: dateRange.lte.format('YYYY-MM-DD'),
        },
      },
    };
  });

  if (loading) {
    return <Loader size={18} />;
  }

  if (error || !data?.PriceService_productPrice) {
    return <TextOnlyErrorMessage>{t('checkout-page:price-calculation-error')}</TextOnlyErrorMessage>;
  }

  return <ProductPrice productPrice={data.PriceService_productPrice} />;
};

export const ProductCardPrice: FC<ProductCardPriceProps> = ({
  showPrices, hasHiddenPrice, productId, dateRange, previewPriceInformation,
}) => {
  const [t] = useTranslation(['checkout-page']);
  const { status } = useSession();

  // Product has hidden price and user is not logged in
  if (false === showPrices && true === hasHiddenPrice) {
    return <CardLoginHint />;
  }

  // Something blocks to show the prices of the product but has an unknown reason.
  if (false === showPrices) {
    return null;
  }

  // if user is not logged in, we show the product price directly
  if (LoginStates.LOGGED_IN === status) {
    return (
      <div className={layout.priceContainer}>
        <DynamicProductCardPrice productId={productId} dateRange={dateRange} />
      </div>
    );
  }

  if (!previewPriceInformation) {
    return <TextOnlyErrorMessage>{t('checkout-page:price-calculation-error')}</TextOnlyErrorMessage>;
  }

  return (
    <div className={layout.priceContainer}>
      <ProductPrice productPrice={previewPriceInformation} />
    </div>
  );
};
